import React, { useMemo, useEffect, useRef, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Divider,
  Alert,
  Button,
  Tabs,
  Tab,
  CircularProgress,
  IconButton,
  TextField,
  Collapse,
  Tooltip,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardActionArea,
  CardContent,
  Radio,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  DirectionsCar as TravelIcon,
  Home as DwellIcon,
  Schedule as TimeIcon,
  LocationOn as LocationIcon,
  PictureAsPdf as PdfIcon,
  Memory as AiIcon,
  Straighten as DistanceIcon,
  NoteAdd as NoteAddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Slideshow as PowerPointIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import EmptyState from '@signals/app-shell/components/common/EmptyState';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import pathfinderApi from '../../services/pathfinderApi';
import notesApi, { Note, NoteCreate } from '@signals/app-shell/services/notesApi';
import { parseBackendDateTime } from '@signals/app-shell/utils/parseDateTime';
import { useParticipantLabels } from '@signals/app-shell/hooks/useParticipantLabels';
import { 
  exportToPowerPoint as generatePowerPoint, 
  captureMapScreenshot, 
  ChartData,
  PowerPointTheme,
  RadioTranscriptLine,
  THEME_DEFINITIONS 
} from '@signals/app-shell/utils/powerpointExport';
import type { PathfinderPttUtterance } from '../../services/pttUtterancesService';
import {
  useNarrativeStore,
  buildStoryAiRequest,
  generateStoryAiSummary,
  type StoryEvent,
  type StoryAiDetail,
} from '../../stores/narrativeStore';
import { parseAiSummary } from './storyAiFormat';

interface StoryModeProps {
  dataPoints: any[];
  dwellLocations: any[];
  currentTime: Date | null;
  isFullExtent?: boolean; // True when showing full extent (all data)
  selectedStudents: string[];
  selectedDevices?: Record<string, string[]>; // studentId -> deviceIds[]
  onMapCenterChange?: (center: [number, number] | null) => void;
  onTimeChange?: (time: Date) => void;
  /** PTT captions for the loaded entities; exported as a "Radio transcript" section. */
  pttUtterances?: PathfinderPttUtterance[];
}

// Shared with the map narrative overlay (see stores/narrativeStore.ts).
type TimelineEvent = StoryEvent;

const StoryMode: React.FC<StoryModeProps> = ({
  dataPoints,
  dwellLocations,
  currentTime,
  isFullExtent = true,
  selectedStudents,
  selectedDevices = {},
  onMapCenterChange,
  onTimeChange,
  pttUtterances = [],
}: StoryModeProps) => {
  const { singularLower, pluralLower } = useParticipantLabels();
  const activeEventRef = useRef<HTMLLIElement>(null);
  const isProcessingRef = useRef(false);
  const lastDataHashRef = useRef<string>('');
  const [activeTab, setActiveTab] = useState(0);
  // AI summary lives in the narrative store so the map overlay (fullscreen)
  // can show and regenerate the same text.
  const aiSummary = useNarrativeStore((s) => s.storyAiSummary);
  const isLoadingAI = useNarrativeStore((s) => s.storyAiLoading);
  const publishStoryEvents = useNarrativeStore((s) => s.setStoryEvents);
  // Focus / detail are shared with the fullscreen HUD dialog so either view
  // regenerates the same way.
  const aiFocus = useNarrativeStore((s) => s.storyAiFocus);
  const aiDetail = useNarrativeStore((s) => s.hudStyle.storyAiDetail);
  const updateHudStyle = useNarrativeStore((s) => s.updateHudStyle);
  const [aiFocusDraft, setAiFocusDraft] = useState('');
  useEffect(() => {
    setAiFocusDraft(aiFocus ?? '');
  }, [aiFocus]);
  const aiBlocks = useMemo(() => (aiSummary ? parseAiSummary(aiSummary) : []), [aiSummary]);
  const [computedTimelineEvents, setComputedTimelineEvents] = useState<TimelineEvent[]>([]);

  // Mirror the computed timeline to the shared store for the map overlay.
  useEffect(() => {
    publishStoryEvents(computedTimelineEvents, selectedStudents);
  }, [computedTimelineEvents, selectedStudents, publishStoryEvents]);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  
  // Export state
  const [isExporting, setIsExporting] = useState(false);
  const [exportStatus, setExportStatus] = useState<string>('');
  
  // Notes state
  const [eventNotes, setEventNotes] = useState<Record<string, Note[]>>({});
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  // PowerPoint theme selection state
  const [themeDialogOpen, setThemeDialogOpen] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState<PowerPointTheme>('standard');

  // Filter dataPoints based on selectedDevices
  // Radio transcript for export: the selected entities, chronological, with a sender label.
  const radioTranscript = useMemo<RadioTranscriptLine[]>(() => {
    if (!pttUtterances.length) return [];
    const selected = new Set(selectedStudents);
    return pttUtterances
      .filter((u) => selected.size === 0 || (u.entity_id && selected.has(u.entity_id)))
      .map((u) => ({
        sender: u.sender || u.entity_id || u.device_id,
        team: u.team,
        startedAt: new Date(u.started_at),
        transcript: (u.transcript || '').trim(),
      }))
      .filter((l) => Number.isFinite(l.startedAt.getTime()))
      .sort((a, b) => a.startedAt.getTime() - b.startedAt.getTime());
  }, [pttUtterances, selectedStudents]);

  const filteredDataPoints = useMemo(() => {
    const selectedDeviceIds = Object.values(selectedDevices).flat();
    
    // If no devices selected, show nothing
    if (selectedDeviceIds.length === 0) {
      return [];
    }
    
    const filtered = dataPoints.filter(point => {
      // Normalize cellphone/smartphone/samsungphone -> "phone" to match the key
      // extractDevicesEfficiently writes into selectedDevices.
      const rawPtType = (point.deviceType || 'phone').toLowerCase();
      const normalizedPtType = rawPtType.includes('phone') ? 'phone' : rawPtType;
      const deviceKey = `${point.studentId}-${normalizedPtType}`;
      return selectedDeviceIds.includes(deviceKey);
    });
    
    return filtered;
  }, [dataPoints, selectedDevices]);

  // Filter dwellLocations based on selectedDevices
  const filteredDwellLocations = useMemo(() => {
    const selectedDeviceIds = Object.values(selectedDevices).flat();
    
    if (selectedDeviceIds.length === 0) {
      return [];
    }
    
    const filtered = dwellLocations.filter(dwell => {
      const deviceKey = `${dwell.entity_id || dwell.studentId}-${(dwell.device_type || 'phone').toLowerCase()}`;
      return selectedDeviceIds.includes(deviceKey);
    });
    
    return filtered;
  }, [dwellLocations, selectedDevices]);

  // Helper functions (defined before useMemo to avoid reference errors)
  const toRad = (degrees: number): number => {
    return (degrees * Math.PI) / 180;
  };

  // Haversine formula for distance between two coordinates
  const haversineDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371; // Earth's radius in km
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  const MAX_HOP_KM = 250;            // ~155 miles in a single tick is implausible
  const MAX_HOP_SPEED_KMH = 500;     // > commercial cruise speed
  const calculateDistance = (points: any[]): number => {
    let totalDistance = 0;
    for (let i = 1; i < points.length; i++) {
      const [lat1, lon1] = points[i - 1].coordinates;
      const [lat2, lon2] = points[i].coordinates;
      if (lat1 == null || lon1 == null || lat2 == null || lon2 == null) continue;
      // Drop obvious sentinel coords (0,0 land in the Gulf of Guinea; if both
      // points are exactly zero we know it's bad data, not a real hop).
      if (lat1 === 0 && lon1 === 0) continue;
      if (lat2 === 0 && lon2 === 0) continue;
      const hop = haversineDistance(lat1, lon1, lat2, lon2);
      if (hop > MAX_HOP_KM) continue;
      const t1 = points[i - 1].timestamp?.getTime?.() ?? 0;
      const t2 = points[i].timestamp?.getTime?.() ?? 0;
      const dtHours = Math.max(1 / 3600, (t2 - t1) / 3600000); // floor at 1s
      if (hop / dtHours > MAX_HOP_SPEED_KMH) continue;
      totalDistance += hop;
    }
    return totalDistance;
  };

  useEffect(() => {
    
    // Only generate timeline if exactly one student is selected
    if (selectedStudents.length !== 1) {
      setComputedTimelineEvents([]);
      return;
    }
    
    // Create a stable hash of the data to prevent unnecessary recomputation
    const selectedDeviceKeys = Object.keys(selectedDevices).sort().join(',');
    const dataHash = `${filteredDataPoints.length}-${filteredDwellLocations.length}-${selectedDeviceKeys}`;
    
    // Skip if data hasn't actually changed
    if (dataHash === lastDataHashRef.current) {
      return;
    }
    
    // Prevent concurrent processing
    if (isProcessingRef.current) {
      return;
    }
    
    lastDataHashRef.current = dataHash;
    isProcessingRef.current = true;
    
    // Run timeline generation synchronously
    try {
      const generateEvents = () => {
    const events: TimelineEvent[] = [];

    const _normKey = (s: any) => (s ? String(s).trim().toLowerCase() : '');
    const _pointEntityRaw = new Set<string>();
    const _pointEntityNorm = new Set<string>();
    filteredDataPoints.forEach(p => {
      const v = (p.studentId || p.deviceId || '') as string;
      if (v) _pointEntityRaw.add(v);
      _pointEntityNorm.add(_normKey(v));
    });
    const _dwellEntityRaw = new Set<string>();
    const _dwellEntityNorm = new Set<string>();
    filteredDwellLocations.forEach(d => {
      const v = (d.entity_id || d.studentId || '') as string;
      if (v) _dwellEntityRaw.add(v);
      _dwellEntityNorm.add(_normKey(v));
    });

    // Add dwell events (using filtered data)
    filteredDwellLocations.forEach(dwell => {
      const start = parseBackendDateTime(dwell.start_time);
      const end = parseBackendDateTime(dwell.end_time);
      if (!start || !end) return;
      events.push({
        id: `dwell-${dwell.id}`,
        type: 'dwell',
        startTime: start,
        endTime: end,
        entityId: dwell.entity_id,
        deviceType: dwell.device_type,
        location: [dwell.latitude, dwell.longitude],
        duration: dwell.duration_seconds,
        address:
          (dwell as any).place_name ||
          (dwell as any).place_address ||
          `${dwell.latitude.toFixed(5)}, ${dwell.longitude.toFixed(5)}`,
      });
    });

    const normalizeDeviceType = (raw: any): string => {
      const v = (raw || 'phone').toString().toLowerCase();
      return v.includes('phone') ? 'phone' : v;
    };
    const sortedPoints = [...filteredDataPoints].sort((a, b) => {
      const entityCompare = (a.studentId || a.deviceId).localeCompare(b.studentId || b.deviceId);
      if (entityCompare !== 0) return entityCompare;
      const deviceCompare = normalizeDeviceType(a.deviceType).localeCompare(normalizeDeviceType(b.deviceType));
      if (deviceCompare !== 0) return deviceCompare;
      return a.timestamp.getTime() - b.timestamp.getTime();
    });

    // OPTIMIZATION: Pre-process dwells into a lookup map by entity ID
    // This reduces complexity from O(points × dwells) to O(points × dwells_per_entity)
    const dwellsByEntity = new Map<string, Array<{ start: number; end: number }>>();
    filteredDwellLocations.forEach(dwell => {
      const start = parseBackendDateTime(dwell.start_time);
      const end = parseBackendDateTime(dwell.end_time);
      if (!start || !end) return;
      
      const entityId = dwell.entity_id;
      if (!dwellsByEntity.has(entityId)) {
        dwellsByEntity.set(entityId, []);
      }
      dwellsByEntity.get(entityId)!.push({
        start: start.getTime(),
        end: end.getTime()
      });
    });

    // Sort each entity's dwells by start time for faster lookup
    dwellsByEntity.forEach(dwells => {
      dwells.sort((a, b) => a.start - b.start);
    });

    // SIMPLIFIED APPROACH: Any GPS point outside a dwell = travel
    // Group consecutive non-dwell points into travel segments
    let currentTravelSegment: any = null;
    const TRAVEL_GAP_THRESHOLD = 10 * 60 * 1000; // 10 minutes gap = new segment

    sortedPoints.forEach((point, _index) => {
      const entityId = point.studentId || point.deviceId;
      // Normalize so cellphone / smartphone / samsungphone all collapse to
      // 'phone' — matches how filteredDataPoints / selectedDevices are keyed.
      const deviceType = normalizeDeviceType(point.deviceType);

      // Check if this point is during a dwell period (optimized lookup)
      const entityDwells = dwellsByEntity.get(entityId) || [];
      const pointTime = point.timestamp.getTime();
      const isDuringDwell = entityDwells.some(dwell => 
        pointTime >= dwell.start && pointTime <= dwell.end
      );

      if (isDuringDwell) {
        // Point is during a dwell - end current travel segment if exists
        if (currentTravelSegment && currentTravelSegment.points.length >= 1) {
          const distance = currentTravelSegment.points.length > 1 
            ? calculateDistance(currentTravelSegment.points)
            : 0;
            
          events.push({
            id: `travel-${currentTravelSegment.startTime.getTime()}`,
            type: 'travel',
            startTime: currentTravelSegment.startTime,
            endTime: currentTravelSegment.lastTime,
            entityId: currentTravelSegment.entityId,
            deviceType: currentTravelSegment.deviceType,
            distance,
            pointCount: currentTravelSegment.points.length,
            startLocation: currentTravelSegment.points[0].coordinates,
            endLocation: currentTravelSegment.points[currentTravelSegment.points.length - 1].coordinates
          });
          currentTravelSegment = null;
        }
        return; // Skip this point (it's part of a dwell)
      }

      if (!currentTravelSegment || 
          currentTravelSegment.entityId !== entityId ||
          currentTravelSegment.deviceType !== deviceType ||
          point.timestamp.getTime() - currentTravelSegment.lastTime.getTime() > TRAVEL_GAP_THRESHOLD) {
        
        // Save previous segment if exists
        if (currentTravelSegment && currentTravelSegment.points.length >= 1) {
          const distance = currentTravelSegment.points.length > 1 
            ? calculateDistance(currentTravelSegment.points)
            : 0;
            
          events.push({
            id: `travel-${currentTravelSegment.startTime.getTime()}`,
            type: 'travel',
            startTime: currentTravelSegment.startTime,
            endTime: currentTravelSegment.lastTime,
            entityId: currentTravelSegment.entityId,
            deviceType: currentTravelSegment.deviceType,
            distance,
            pointCount: currentTravelSegment.points.length,
            startLocation: currentTravelSegment.points[0].coordinates,
            endLocation: currentTravelSegment.points[currentTravelSegment.points.length - 1].coordinates
          });
        }

        // Start new travel segment
        currentTravelSegment = {
          entityId,
          deviceType,
          startTime: point.timestamp,
          lastTime: point.timestamp,
          points: [point]
        };
      } else {
        // Continue current travel segment
        currentTravelSegment.lastTime = point.timestamp;
        currentTravelSegment.points.push(point);
      }
    });

    // Add last segment if exists
    if (currentTravelSegment && currentTravelSegment.points.length >= 1) {
      const distance = currentTravelSegment.points.length > 1 
        ? calculateDistance(currentTravelSegment.points)
        : 0;
        
      events.push({
        id: `travel-${currentTravelSegment.startTime.getTime()}`,
        type: 'travel',
        startTime: currentTravelSegment.startTime,
        endTime: currentTravelSegment.lastTime,
        entityId: currentTravelSegment.entityId,
        deviceType: currentTravelSegment.deviceType,
        distance,
        pointCount: currentTravelSegment.points.length,
        startLocation: currentTravelSegment.points[0].coordinates,
        endLocation: currentTravelSegment.points[currentTravelSegment.points.length - 1].coordinates
      });
    }

    // Infer travel between consecutive dwells if there's a time gap
    // Sort dwells by entity and time (using filtered data)
    const dwellsByEntityForGaps = filteredDwellLocations.reduce((acc: any, dwell) => {
      const key = dwell.entity_id;
      if (!acc[key]) acc[key] = [];
      acc[key].push({
        ...dwell,
        startTime: parseBackendDateTime(dwell.start_time),
        endTime: parseBackendDateTime(dwell.end_time)
      });
      return acc;
    }, {});

    Object.entries(dwellsByEntityForGaps).forEach(([entityId, dwells]: [string, any]) => {
      const sortedDwells = dwells.sort((a: any, b: any) => a.startTime.getTime() - b.startTime.getTime());
      
      for (let i = 0; i < sortedDwells.length - 1; i++) {
        const currentDwell = sortedDwells[i];
        const nextDwell = sortedDwells[i + 1];
        
        // Check if there's a gap between dwells (travel time)
        const gapStart = currentDwell.endTime;
        const gapEnd = nextDwell.startTime;
        const gapDuration = gapEnd.getTime() - gapStart.getTime();
        
        // If gap is more than 1 minute and less than 2 hours, check if we should infer travel
        if (gapDuration > 60 * 1000 && gapDuration < 2 * 60 * 60 * 1000) {
          // Check if we already have GPS-based travel events that overlap this gap
          const hasExistingTravel = events.some(event => {
            if (event.type !== 'travel' || event.entityId !== entityId) return false;
            
            // Check for ANY overlap between travel event and gap
            const travelStart = event.startTime.getTime();
            const travelEnd = event.endTime.getTime();
            const gapS = gapStart.getTime();
            const gapE = gapEnd.getTime();
            
            // Overlap exists if: travel starts before gap ends AND travel ends after gap starts
            return travelStart < gapE && travelEnd > gapS;
          });
          
          if (!hasExistingTravel) {
            // Calculate distance between dwells
            const distance = haversineDistance(
              currentDwell.latitude,
              currentDwell.longitude,
              nextDwell.latitude,
              nextDwell.longitude
            );
            
            // Only add inferred travel if distance is meaningful (> 50m)
            if (distance > 0.05) {
              events.push({
                id: `travel-inferred-${gapStart.getTime()}`,
                type: 'travel',
                startTime: gapStart,
                endTime: gapEnd,
                entityId: entityId,
                deviceType: currentDwell.device_type,
                distance,
                pointCount: 0, // Inferred, no actual GPS points
                startLocation: [currentDwell.latitude, currentDwell.longitude],
                endLocation: [nextDwell.latitude, nextDwell.longitude]
              });
            }
          }
        }
      }
    });

    const sortedEvents = events.sort((a, b) => {
      const startDiff = a.startTime.getTime() - b.startTime.getTime();
      if (startDiff !== 0) return startDiff;
      if (a.type !== b.type) return a.type === 'dwell' ? -1 : 1;
      const durA = a.endTime.getTime() - a.startTime.getTime();
      const durB = b.endTime.getTime() - b.startTime.getTime();
      return durB - durA;
    });
    
    // CONSOLIDATION: Group similar dwells and routes to reduce clutter
    const DWELL_CONSOLIDATION_DISTANCE = 0.1; // 100 meters in km
    const ROUTE_CONSOLIDATION_DISTANCE = 0.5; // 500 meters in km
    const consolidatedEvents: TimelineEvent[] = [];

    const eventOverlapsTimeGap = (
      gapStartMs: number,
      gapEndMs: number,
      event: TimelineEvent
    ): boolean => {
      const eStart = event.startTime.getTime();
      const eEnd = event.endTime.getTime();
      return eStart < gapEndMs && eEnd > gapStartMs;
    };

    const hasInterveningLocationBreak = (
      priorDwell: TimelineEvent,
      currentDwell: TimelineEvent,
      anchorLocation: [number, number],
      eventsSoFar: TimelineEvent[]
    ): boolean => {
      const gapStart = priorDwell.endTime.getTime();
      const gapEnd = currentDwell.startTime.getTime();
      if (gapEnd <= gapStart) return false;

      return eventsSoFar.some((e) => {
        if (e === priorDwell || !eventOverlapsTimeGap(gapStart, gapEnd, e)) return false;
        if (e.type === 'travel') return true;
        if (e.type === 'dwell' && e.location) {
          const dist = haversineDistance(
            anchorLocation[0], anchorLocation[1],
            e.location[0], e.location[1]
          );
          return dist > DWELL_CONSOLIDATION_DISTANCE;
        }
        return false;
      });
    };
    
    for (let i = 0; i < sortedEvents.length; i++) {
      const currentEvent = sortedEvents[i];
      let shouldAdd = true;
      
      // Try to consolidate with existing events
      for (let j = consolidatedEvents.length - 1; j >= 0; j--) {
        const existingEvent = consolidatedEvents[j];
        
        // Only consolidate same entity and same type
        if (existingEvent.entityId !== currentEvent.entityId || 
            existingEvent.type !== currentEvent.type) {
          continue;
        }
        
        // Check if events are on the same day
        const sameDay = existingEvent.startTime.toDateString() === currentEvent.startTime.toDateString();
        if (!sameDay) continue;
        
        // Consolidate dwells at same location
        if (currentEvent.type === 'dwell' && currentEvent.location && existingEvent.location) {
          const dist = haversineDistance(
            existingEvent.location[0], existingEvent.location[1],
            currentEvent.location[0], currentEvent.location[1]
          );
          
          if (dist <= DWELL_CONSOLIDATION_DISTANCE) {
            if (
              hasInterveningLocationBreak(
                existingEvent,
                currentEvent,
                existingEvent.location,
                consolidatedEvents
              )
            ) {
              continue;
            }

            // Merge this dwell into the existing one
            const totalDuration = (existingEvent.duration || 0) + (currentEvent.duration || 0);
            const visitCount = existingEvent.visitCount || 1;
            
            consolidatedEvents[j] = {
              ...existingEvent,
              endTime: currentEvent.endTime, // Extend to latest end time
              duration: totalDuration,
              visitCount: visitCount + 1,
              // Update ID to show it's consolidated
              id: `${existingEvent.id}-consolidated`
            };
            
            shouldAdd = false;
            break;
          }
        }
        
        // Consolidate travel segments with similar start/end points
        if (currentEvent.type === 'travel' && currentEvent.startLocation && currentEvent.endLocation &&
            existingEvent.startLocation && existingEvent.endLocation) {
          const startDist = haversineDistance(
            existingEvent.startLocation[0], existingEvent.startLocation[1],
            currentEvent.startLocation[0], currentEvent.startLocation[1]
          );
          const endDist = haversineDistance(
            existingEvent.endLocation[0], existingEvent.endLocation[1],
            currentEvent.endLocation[0], currentEvent.endLocation[1]
          );
          
          if (startDist <= ROUTE_CONSOLIDATION_DISTANCE && endDist <= ROUTE_CONSOLIDATION_DISTANCE) {
            // Merge this travel into the existing one
            const totalDistance = (existingEvent.distance || 0) + (currentEvent.distance || 0);
            const totalPoints = (existingEvent.pointCount || 0) + (currentEvent.pointCount || 0);
            const tripCount = existingEvent.tripCount || 1;
            
            consolidatedEvents[j] = {
              ...existingEvent,
              endTime: currentEvent.endTime,
              distance: totalDistance,
              pointCount: totalPoints,
              tripCount: tripCount + 1,
              id: `${existingEvent.id}-consolidated`
            };
            
            shouldAdd = false;
            break;
          }
        }
      }
      
      if (shouldAdd) {
        consolidatedEvents.push(currentEvent);
      }
    }
    
    const SAME_ENTITY_OVERLAP_SLACK_MS = 60 * 1000; // 1 minute
    const dwellsForOverlap = consolidatedEvents.filter((e) => e.type === 'dwell');
    const normalizeEntityKey = (s: any) => (s ? String(s).trim().toLowerCase() : '');

    const _diagTravelTrace: any[] = [];
    const deOverlappedEvents = consolidatedEvents.filter((event) => {
      if (event.type !== 'travel') return true;
      const evtEntity = normalizeEntityKey(event.entityId);
      const evtStart = event.startTime.getTime();
      const evtEnd = event.endTime.getTime();
      const _comparisons = dwellsForOverlap.map((dwell) => {
        const dwellEntityNorm = normalizeEntityKey(dwell.entityId);
        const entityMatches = dwellEntityNorm === evtEntity;
        const dStart = dwell.startTime.getTime() - SAME_ENTITY_OVERLAP_SLACK_MS;
        const dEnd = dwell.endTime.getTime() + SAME_ENTITY_OVERLAP_SLACK_MS;
        const timeContained = evtStart >= dStart && evtEnd <= dEnd;
        return {
          dwellId: dwell.id,
          dwellEntityRaw: dwell.entityId,
          dwellEntityNorm,
          entityMatches,
          dwellStart: dwell.startTime.toISOString(),
          dwellEnd: dwell.endTime.toISOString(),
          timeContained,
          dropReason: entityMatches && timeContained ? 'DROP' : (
            !entityMatches ? 'entity-mismatch' : 'time-outside-window'
          ),
        };
      });
      const contained = _comparisons.some(c => c.entityMatches && c.timeContained);
      _diagTravelTrace.push({
        travelId: event.id,
        travelEntityRaw: event.entityId,
        travelEntityNorm: evtEntity,
        travelStart: event.startTime.toISOString(),
        travelEnd: event.endTime.toISOString(),
        contained,
        comparisons: _comparisons,
      });
      return !contained;
    });

    return deOverlappedEvents;
      };
      
      // Compute the events
      const events = generateEvents();
      setComputedTimelineEvents(events);
      isProcessingRef.current = false;
    } catch (error) {
      console.error('Error generating timeline:', error);
      isProcessingRef.current = false;
      setComputedTimelineEvents([]);
    }
  }, [filteredDataPoints, filteredDwellLocations, selectedDevices, selectedStudents.length]);

  // Find active event based on current time
  const activeEventIndex = useMemo(() => {
    if (!currentTime) return -1;
    
    // When showing full extent, highlight the first event (beginning of story)
    if (isFullExtent && computedTimelineEvents.length > 0) {
      return 0;
    }
    
    // When playing or scrubbing timeline, find the event at current time
    const currentTimeMs = currentTime.getTime();
    const index = computedTimelineEvents.findIndex(event => {
      const startMs = event.startTime.getTime();
      const endMs = event.endTime.getTime();
      return currentTimeMs >= startMs && currentTimeMs <= endMs;
    });
    
    return index;
  }, [computedTimelineEvents, currentTime, isFullExtent]);

  // Auto-scroll to active event
  useEffect(() => {
    if (activeEventRef.current) {
      activeEventRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  }, [activeEventIndex]);

  // Format duration
  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  // Format distance
  const formatDistance = (km: number): string => {
    const miles = km * 0.621371;
    if (miles < 0.1) {
      return `${Math.round(miles * 5280)}ft`; // Convert to feet for very short distances
    }
    return `${miles.toFixed(2)}mi`;
  };

  // Generate AI summary of timeline
  const generateAISummary = useCallback(async () => {
    if (computedTimelineEvents.length === 0) return;
    // Make sure the store has the current events before generating.
    publishStoryEvents(computedTimelineEvents, selectedStudents);
    await generateStoryAiSummary({ focus: aiFocusDraft, detail: aiDetail });
  }, [computedTimelineEvents, selectedStudents, publishStoryEvents, aiFocusDraft, aiDetail]);

  // Click handler for timeline events
  const handleEventClick = useCallback((event: TimelineEvent) => {
    // Toggle selection: clicking the same event again restores the original map extent
    const isSame = selectedEventId === event.id;
    setSelectedEventId(isSame ? null : event.id);
    if (isSame) {
      if (onMapCenterChange) onMapCenterChange(null);
      return;
    }
    
    // Get the location for this event
    let location: [number, number] | undefined;
    
    if (event.type === 'dwell' && event.location) {
      location = event.location;
    } else if (event.type === 'travel' && event.startLocation) {
      location = event.startLocation; // Start of travel
    }
    
    // Update map center if location exists
    if (location && onMapCenterChange) {
      onMapCenterChange(location);
    }
    
    // Update time slider to event start time
    if (onTimeChange) {
      onTimeChange(event.startTime);
    }
  }, [onMapCenterChange, onTimeChange, selectedEventId]);

  // Load notes for timeline events
  const loadNotesForEvents = useCallback(async () => {
    if (computedTimelineEvents.length === 0) return;
    
    try {
      // Fetch all notes for 'entity' type that match our timeline event IDs
      const response = await notesApi.listNotes({
        entity_type: 'entity',
        page_size: 500
      });
      
      // Group notes by entity_id (which matches our event.id)
      const notesMap: Record<string, Note[]> = {};
      response.items.forEach(note => {
        if (!notesMap[note.entity_id]) {
          notesMap[note.entity_id] = [];
        }
        notesMap[note.entity_id].push(note);
      });
      
      setEventNotes(notesMap);
    } catch (error) {
      console.error('Failed to load notes:', error);
    }
  }, [computedTimelineEvents.length]);

  // Load notes when timeline events change
  useEffect(() => {
    loadNotesForEvents();
  }, [loadNotesForEvents]);

  // Start editing a note for an event
  const handleStartAddNote = (eventId: string) => {
    setEditingNoteId(eventId);
    setNoteText('');
  };

  // Cancel note editing
  const handleCancelNote = () => {
    setEditingNoteId(null);
    setNoteText('');
  };

  // Save note
  const handleSaveNote = async (event: TimelineEvent) => {
    if (!noteText.trim()) return;
    
    setSavingNote(true);
    try {
      const noteData: NoteCreate = {
        entity_type: 'entity',
        entity_id: event.id,
        entity_label: `${event.type === 'dwell' ? 'Dwell' : 'Travel'} - ${event.entityId}`,
        content: noteText.trim(),
        priority: 'normal',
        reference_date: event.startTime.toISOString(),
        latitude: event.location?.[0] || event.startLocation?.[0],
        longitude: event.location?.[1] || event.startLocation?.[1]
      };
      
      const savedNote = await notesApi.createNote(noteData);
      
      // Update local state
      setEventNotes(prev => ({
        ...prev,
        [event.id]: [...(prev[event.id] || []), savedNote]
      }));
      
      setEditingNoteId(null);
      setNoteText('');
    } catch (error) {
      console.error('Failed to save note:', error);
    } finally {
      setSavingNote(false);
    }
  };

  // Delete note
  const handleDeleteNote = async (noteId: number, eventId: string) => {
    try {
      await notesApi.deleteNote(noteId);
      setEventNotes(prev => ({
        ...prev,
        [eventId]: (prev[eventId] || []).filter(n => n.id !== noteId)
      }));
    } catch (error) {
      console.error('Failed to delete note:', error);
    }
  };

  // Check if exactly one student is selected (no co-travelers)
  if (selectedStudents.length !== 1) {
    return (
      <Box sx={{ p: 1.5 }}>
        <EmptyState
          dense
          icon={<TimeIcon />}
          title={`Select one ${singularLower}`}
          description={
            selectedStudents.length === 0
              ? `Story Mode shows a single ${singularLower}'s timeline. Select a ${singularLower} to begin.`
              : `You have ${selectedStudents.length} ${pluralLower} selected. Story Mode needs exactly one — select a single ${singularLower}.`
          }
        />
      </Box>
    );
  }

  if (computedTimelineEvents.length === 0) {
    return (
      <Box sx={{ p: 1.5 }}>
        <EmptyState
          dense
          icon={<TimeIcon />}
          title="No timeline events yet"
          description={
            filteredDataPoints.length === 0 && filteredDwellLocations.length === 0
              ? 'Load track and dwell data to see the story timeline.'
              : `Found ${filteredDataPoints.length} data points and ${filteredDwellLocations.length} dwells, processing events…`
          }
        />
      </Box>
    );
  }

  // Export to PDF function
  const exportToPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Title
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('Story Mode Timeline Report', pageWidth / 2, 20, { align: 'center' });
    
    // Subtitle
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, 28, { align: 'center' });
    doc.text(`Entities: ${selectedStudents.join(', ')}`, pageWidth / 2, 34, { align: 'center' });
    doc.text(`Total Events: ${computedTimelineEvents.length}`, pageWidth / 2, 40, { align: 'center' });
    
    // Prepare table data
    const tableData = computedTimelineEvents.map((event, index) => {
      const duration = (event.endTime.getTime() - event.startTime.getTime()) / 1000;
      const durationStr = duration >= 3600 
        ? `${(duration / 3600).toFixed(1)}h`
        : duration >= 60 
          ? `${(duration / 60).toFixed(0)}m`
          : `${duration.toFixed(0)}s`;
      
      const startStr = `${event.startTime.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} ${event.startTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
      const endStr = `${event.endTime.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} ${event.endTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
      
      let details = '';
      if (event.type === 'travel') {
        details = event.distance 
          ? `${event.distance.toFixed(2)} km, ${event.pointCount} GPS points`
          : `${event.pointCount} GPS points`;
      } else {
        details = event.address || `${event.location?.[0].toFixed(5)}, ${event.location?.[1].toFixed(5)}`;
      }
      
      return [
        index + 1,
        event.type === 'travel' ? 'Traveling' : 'Dwelling',
        startStr,
        endStr,
        durationStr,
        `${event.entityId} (${event.deviceType})`,
        details
      ];
    });
    
    // Add table
    autoTable(doc, {
      startY: 48,
      head: [['#', 'Type', 'Start', 'End', 'Duration', 'Entity', 'Details']],
      body: tableData,
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [41, 128, 185], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      columnStyles: {
        0: { cellWidth: 10 },
        1: { cellWidth: 25 },
        2: { cellWidth: 35 },
        3: { cellWidth: 35 },
        4: { cellWidth: 20 },
        5: { cellWidth: 35 },
        6: { cellWidth: 'auto' }
      },
      didDrawPage: (data) => {
        // Footer
        const pageCount = (doc as any).internal.pages.length - 1;
        doc.setFontSize(8);
        doc.text(
          `Page ${data.pageNumber} of ${pageCount}`,
          pageWidth / 2,
          doc.internal.pageSize.getHeight() - 10,
          { align: 'center' }
        );
      }
    });

    // Radio transcript (PTT captions) for the same entities and range
    if (radioTranscript.length > 0) {
      doc.addPage();
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Radio Transcript', pageWidth / 2, 20, { align: 'center' });
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`${radioTranscript.length} transmissions`, pageWidth / 2, 27, { align: 'center' });
      autoTable(doc, {
        startY: 34,
        head: [['Time', 'Sender', 'Team', 'Transcript']],
        body: radioTranscript.map((l) => [
          `${l.startedAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} ${l.startedAt.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`,
          l.sender,
          l.team || '',
          l.transcript || '(no transcript)',
        ]),
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [41, 128, 185], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        columnStyles: {
          0: { cellWidth: 32 },
          1: { cellWidth: 32 },
          2: { cellWidth: 20 },
          3: { cellWidth: 'auto' },
        },
        didDrawPage: (data) => {
          const pageCount = (doc as any).internal.pages.length - 1;
          doc.setFontSize(8);
          doc.text(`Page ${data.pageNumber} of ${pageCount}`, pageWidth / 2, doc.internal.pageSize.getHeight() - 10, { align: 'center' });
        },
      });
    }
    
    // Save PDF
    const filename = `story-timeline-${selectedStudents.join('-')}-${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(filename);
  };

  // Open theme selection dialog
  const handlePowerPointClick = () => {
    setThemeDialogOpen(true);
  };

  // Export to PowerPoint function (called after theme selection)
  const exportToPowerPoint = async (theme: PowerPointTheme) => {
    setThemeDialogOpen(false);
    setIsExporting(true);
    setExportStatus('Capturing map screenshot...');
    
    try {
      // Prepare dwell points for numbering overlays in exported slides
      const dwellPointsForProjection = computedTimelineEvents
        .filter(e => e.type === 'dwell' && e.location)
        .map(e => ({
          id: e.id,
          lat: e.location![0],
          lon: e.location![1]
        }));

      // Capture map screenshot with dimensions + projected dwell pixel coordinates
      const mapScreenshot = await captureMapScreenshot('pathfinder-map-container', dwellPointsForProjection);

      // Calculate chart data from available data
      setExportStatus('Processing chart data...');
      const chartData: ChartData = calculateChartData();

      // Auto-generate AI narrative for the export
      let aiNarrative: string | undefined;
      if (computedTimelineEvents.length > 0) {
        try {
          setExportStatus('Generating AI insights... (this may take a moment)');
          
          const summary = await pathfinderApi.generateStoryModeAI(
            buildStoryAiRequest(computedTimelineEvents, selectedStudents)
          );
          
          aiNarrative = summary.summary;
        } catch (aiError) {
          console.warn('AI narrative generation failed, continuing without it:', aiError);
          setExportStatus('AI generation skipped, building presentation...');
        }
      }

      // Export to PowerPoint
      setExportStatus(`Building PowerPoint presentation (${THEME_DEFINITIONS[theme].name})...`);
      await generatePowerPoint({
        title: 'Pathfinder Story Timeline Report',
        subtitle: `Generated ${new Date().toLocaleDateString()}`,
        // The left panel never carries camera entries (fullscreen HUD only); narrow the type for the exporter.
        events: computedTimelineEvents.flatMap((e) => (e.type === 'camera' ? [] : [{ ...e, type: e.type }])),
        entities: selectedStudents,
        eventNotes: Object.fromEntries(
          Object.entries(eventNotes).map(([eventId, notes]) => [
            eventId,
            (notes as Note[]).map(n => ({ id: n.id, content: n.content }))
          ])
        ),
        mapScreenshot,
        chartData,
        aiNarrative,
        radioTranscript,
        includeMap: true,
        includeSummary: true,
        includeTimeline: true,
        includeCharts: true,
        theme
      });
      
      setExportStatus('Export complete!');
      setTimeout(() => {
        setIsExporting(false);
        setExportStatus('');
      }, 1500);
    } catch (error) {
      console.error('Failed to export to PowerPoint:', error);
      setIsExporting(false);
      setExportStatus('');
      alert('Failed to export to PowerPoint. Please try again.');
    }
  };

  // Calculate chart data for PowerPoint export
  const calculateChartData = (): ChartData => {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    
    // Daily rhythm (activity by hour)
    const hourCounts: Record<number, number> = {};
    for (let h = 0; h < 24; h++) hourCounts[h] = 0;
    filteredDataPoints.forEach(point => {
      const hour = point.timestamp.getHours();
      hourCounts[hour]++;
    });
    const dailyRhythm = Array.from({ length: 24 }, (_, h) => ({
      hour: `${h.toString().padStart(2, '0')}:00`,
      activity: hourCounts[h]
    }));

    // Weekly activity
    const dayCounts: Record<string, number> = {};
    days.forEach(d => dayCounts[d] = 0);
    filteredDataPoints.forEach(point => {
      const day = days[point.timestamp.getDay()];
      dayCounts[day]++;
    });
    const weeklyActivity = days.map(day => ({
      day,
      activity: dayCounts[day]
    }));

    // Top locations from dwells
    const locationData: Record<string, { count: number; duration: number }> = {};
    filteredDwellLocations.forEach(dwell => {
      const name = dwell.place_name || dwell.place_address || 
        `${dwell.latitude?.toFixed(4)}, ${dwell.longitude?.toFixed(4)}`;
      
      if (!locationData[name]) locationData[name] = { count: 0, duration: 0 };
      locationData[name].count++;
      locationData[name].duration += (dwell.duration_seconds || 0) / 60; // Convert to minutes
    });
    
    const topLocations = Object.entries(locationData)
      .map(([location, data]) => ({
        location: location.length > 30 ? location.substring(0, 28) + '...' : location,
        visits: data.count,
        duration: Math.round(data.duration)
      }))
      .sort((a, b) => b.duration - a.duration)
      .slice(0, 15);

    // Find peak hour
    const peakHourEntry = Object.entries(hourCounts).sort((a, b) => b[1] - a[1])[0];
    const peakHour = peakHourEntry && peakHourEntry[1] > 0 ? parseInt(peakHourEntry[0]) : null;

    // Statistics
    const uniqueDevices = new Set(filteredDataPoints.map(p => p.studentId || p.deviceId));
    const stats = {
      totalPoints: filteredDataPoints.length,
      uniqueDevices: uniqueDevices.size,
      totalDwells: filteredDwellLocations.length,
      peakHour,
      avgPointsPerDay: filteredDataPoints.length > 0 
        ? Math.round(filteredDataPoints.length / Math.max(1, 
            Math.ceil((Math.max(...filteredDataPoints.map(p => p.timestamp.getTime())) - 
                       Math.min(...filteredDataPoints.map(p => p.timestamp.getTime()))) / (1000 * 60 * 60 * 24))))
        : 0
    };

    return { dailyRhythm, weeklyActivity, topLocations, stats };
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 1.5, position: 'relative' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mb: 0 }}>
            📖 Story Timeline
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {computedTimelineEvents.length} events • {selectedStudents.join(', ')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            startIcon={<PdfIcon />}
            onClick={exportToPDF}
            sx={{ minWidth: 100 }}
          >
            PDF
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={isExporting ? <CircularProgress size={16} color="inherit" /> : <PowerPointIcon />}
            onClick={handlePowerPointClick}
            disabled={isExporting}
            sx={{ minWidth: 120 }}
          >
            {isExporting ? 'Exporting...' : 'PowerPoint'}
          </Button>
        </Box>
      </Box>

      {/* Info box explaining travel vs dwell */}
      <Alert severity="info" sx={{ py: 0.5, mb: 1, fontSize: '0.75rem' }} icon={false}>
        <Typography variant="caption" sx={{ display: 'block', fontWeight: 500 }}>
          <strong>Dwelling</strong> = Stayed in one place • <strong>Traveling</strong> = GPS points between dwells
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', fontWeight: 400, color: 'rgba(255,255,255,0.6)' }}>
          Similar dwells (&lt;100m) on the same day consolidate unless another visit or travel occurs between them
        </Typography>
      </Alert>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(_e, newValue) => setActiveTab(newValue)}
        sx={{ borderBottom: 1, borderColor: 'divider', minHeight: 36 }}
      >
        <Tab label="Timeline" sx={{ minHeight: 36, py: 0.5, fontSize: '0.8rem' }} />
        <Tab label="AI Insights" sx={{ minHeight: 36, py: 0.5, fontSize: '0.8rem' }} />
      </Tabs>

      {/* Timeline Tab */}
      {activeTab === 0 && (
        <List sx={{ flex: 1, overflow: 'auto', mt: 1 }}>
        {computedTimelineEvents.map((event, index) => {
          const isActive = index === activeEventIndex;
          const duration = (event.endTime.getTime() - event.startTime.getTime()) / 1000;
          const notes = eventNotes[event.id] || [];
          const isEditingThis = editingNoteId === event.id;

          return (
            <React.Fragment key={event.id}>
              <ListItem
                ref={isActive ? activeEventRef : null}
                sx={{
                  // Fixed background - not using theme/user settings
                  bgcolor: isActive 
                    ? 'rgba(25, 118, 210, 0.15)' 
                    : event.type === 'dwell' 
                      ? 'rgba(30, 40, 55, 0.95)' 
                      : 'rgba(25, 35, 50, 0.95)',
                  borderRadius: 1.5,
                  mb: 0.5,
                  transition: 'background-color 0.3s, transform 0.2s, box-shadow 0.2s',
                  opacity: currentTime && currentTime < event.startTime ? 0.5 : 1,
                  border: isActive ? '2px solid rgba(25, 118, 210, 0.8)' : '1px solid rgba(255, 255, 255, 0.1)',
                  cursor: 'pointer',
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  py: 0.75,
                  px: 1,
                  '&:hover': {
                    bgcolor: isActive 
                      ? 'rgba(25, 118, 210, 0.2)' 
                      : 'rgba(40, 50, 65, 0.95)',
                    transform: 'translateX(4px)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                  }
                }}
              >
                {/* Main content row */}
                <Box 
                  sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}
                  onClick={() => handleEventClick(event)}
                >
                  <ListItemIcon sx={{ minWidth: 32, mt: 0 }}>
                    {event.type === 'dwell' ? (
                      <DwellIcon sx={{ color: isActive ? '#1976d2' : '#90caf9' }} />
                    ) : (
                      <TravelIcon sx={{ color: isActive ? '#1976d2' : '#81c784' }} />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography 
                          variant="body2" 
                          fontWeight={isActive ? 'bold' : 'medium'} 
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 0.5,
                            color: '#fff',
                            fontSize: '0.85rem'
                          }}
                        >
                          {event.type === 'dwell' ? 'Dwelling' : 'Traveling'}
                        </Typography>
                        <Chip
                          label={event.deviceType}
                          size="small"
                          sx={{
                            bgcolor: 'rgba(25, 118, 210, 0.3)',
                            color: '#90caf9',
                            borderColor: 'rgba(25, 118, 210, 0.5)',
                            fontSize: '0.7rem'
                          }}
                          variant="outlined"
                        />
                        {/* Show consolidation badge */}
                        {event.visitCount && event.visitCount > 1 && (
                          <Chip
                            label={`${event.visitCount} visits`}
                            size="small"
                            sx={{
                              bgcolor: 'rgba(255, 183, 77, 0.3)',
                              color: '#ffb74d',
                              borderColor: 'rgba(255, 183, 77, 0.5)',
                              fontSize: '0.7rem',
                              fontWeight: 'bold'
                            }}
                            variant="outlined"
                          />
                        )}
                        {event.tripCount && event.tripCount > 1 && (
                          <Chip
                            label={`${event.tripCount} trips`}
                            size="small"
                            sx={{
                              bgcolor: 'rgba(129, 199, 132, 0.3)',
                              color: '#81c784',
                              borderColor: 'rgba(129, 199, 132, 0.5)',
                              fontSize: '0.7rem',
                              fontWeight: 'bold'
                            }}
                            variant="outlined"
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box sx={{ mt: 0.25 }}>
                        {/* Time range */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                          <TimeIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.6)' }} />
                          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                            {event.startTime.toLocaleTimeString()} - {event.endTime.toLocaleTimeString()}
                            {' '}({formatDuration(duration)})
                          </Typography>
                        </Box>

                        {/* Location info */}
                        {event.type === 'dwell' && event.location && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <LocationIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,0.6)' }} />
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                              {event.address}
                            </Typography>
                          </Box>
                        )}

                        {/* Travel info */}
                        {event.type === 'travel' && event.distance !== undefined && (
                          <Box sx={{ display: 'flex', gap: 2 }}>
                            <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'rgba(255,255,255,0.7)' }}>
                              <DistanceIcon sx={{ fontSize: 12 }} /> Distance: {formatDistance(event.distance)}
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'rgba(255,255,255,0.7)' }}>
                              <LocationIcon sx={{ fontSize: 12 }} /> {event.pointCount} GPS points
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    }
                  />
                  
                  {/* Add note button */}
                  <Tooltip title="Add note">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStartAddNote(event.id);
                      }}
                      sx={{ 
                        color: 'rgba(255,255,255,0.5)',
                        '&:hover': { color: '#ffb74d' }
                      }}
                    >
                      <NoteAddIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>

                {/* Notes section - displayed at bottom in red */}
                {notes.length > 0 && (
                  <Box sx={{ 
                    mt: 0.75, 
                    pt: 0.5, 
                    borderTop: '1px solid rgba(255,255,255,0.1)',
                    width: '100%'
                  }}>
                    {notes.map(note => (
                      <Box 
                        key={note.id} 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'flex-start', 
                          gap: 1,
                          mb: 0.5 
                        }}
                      >
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: '#ef5350',
                            fontWeight: 500,
                            flex: 1,
                            fontStyle: 'italic',
                            lineHeight: 1.4
                          }}
                        >
                          📝 {note.content}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteNote(note.id, event.id);
                          }}
                          sx={{ 
                            color: 'rgba(255,255,255,0.3)',
                            p: 0.25,
                            '&:hover': { color: '#ef5350' }
                          }}
                        >
                          <DeleteIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}

                {/* Note input form */}
                <Collapse in={isEditingThis}>
                  <Box 
                    sx={{ 
                      mt: 0.75, 
                      pt: 0.5, 
                      borderTop: '1px solid rgba(255,255,255,0.1)',
                      width: '100%'
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Add a note..."
                      value={noteText}
                      onChange={(e) => setNoteText(e.target.value)}
                      multiline
                      maxRows={3}
                      autoFocus
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          bgcolor: 'rgba(0,0,0,0.2)',
                          color: '#fff',
                          fontSize: '0.85rem',
                          '& fieldset': {
                            borderColor: 'rgba(255,255,255,0.2)',
                          },
                          '&:hover fieldset': {
                            borderColor: 'rgba(255,255,255,0.3)',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#ef5350',
                          }
                        },
                        '& .MuiInputBase-input::placeholder': {
                          color: 'rgba(255,255,255,0.4)',
                          opacity: 1
                        }
                      }}
                    />
                    <Box sx={{ display: 'flex', gap: 1, mt: 0.5, justifyContent: 'flex-end' }}>
                      <Button
                        size="small"
                        onClick={handleCancelNote}
                        startIcon={<CloseIcon />}
                        sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem' }}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => handleSaveNote(event)}
                        disabled={!noteText.trim() || savingNote}
                        startIcon={<SaveIcon />}
                        sx={{ 
                          bgcolor: '#ef5350',
                          fontSize: '0.75rem',
                          '&:hover': { bgcolor: '#f44336' }
                        }}
                      >
                        {savingNote ? 'Saving...' : 'Save'}
                      </Button>
                    </Box>
                  </Box>
                </Collapse>
              </ListItem>
              {index < computedTimelineEvents.length - 1 && <Divider sx={{ my: 0.25, borderColor: 'rgba(255,255,255,0.05)' }} />}
            </React.Fragment>
          );
        })}
        </List>
      )}

      {/* AI Insights Tab */}
      {activeTab === 1 && (
        <Box sx={{ flex: 1, overflow: 'auto', mt: 2 }}>
          {/* Controls: optional focus + short/detailed. Same inputs the fullscreen HUD dialog sends. */}
          <Box
            sx={{
              p: 1.5,
              mb: 1.5,
              borderRadius: 1.5,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'rgba(255,255,255,0.02)',
            }}
          >
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.75 }}>
              Focus (optional) — e.g. "how many cameras did the subject pass" or "interactions with co-travelers"
            </Typography>
            <TextField
              size="small"
              fullWidth
              multiline
              minRows={1}
              maxRows={4}
              placeholder="Summarize the whole timeline"
              value={aiFocusDraft}
              onChange={(e) => setAiFocusDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && !isLoadingAI) {
                  e.preventDefault();
                  generateAISummary();
                }
              }}
              disabled={isLoadingAI}
              sx={{ mb: 1.25 }}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={aiDetail}
                onChange={(_e, v: StoryAiDetail | null) => {
                  if (v) updateHudStyle({ storyAiDetail: v });
                }}
                disabled={isLoadingAI}
              >
                <ToggleButton value="short" sx={{ px: 1.25, textTransform: 'none', fontSize: '0.75rem' }}>
                  Concise
                </ToggleButton>
                <ToggleButton value="detailed" sx={{ px: 1.25, textTransform: 'none', fontSize: '0.75rem' }}>
                  Detailed
                </ToggleButton>
              </ToggleButtonGroup>
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={isLoadingAI ? <CircularProgress size={14} color="inherit" /> : <AiIcon />}
                onClick={generateAISummary}
                disabled={isLoadingAI || computedTimelineEvents.length === 0}
              >
                {isLoadingAI ? 'Analyzing…' : aiSummary ? 'Regenerate' : 'Generate AI Summary'}
              </Button>
            </Box>
          </Box>

          {isLoadingAI && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={40} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Analyzing {computedTimelineEvents.length} timeline events{aiFocusDraft.trim() ? ` — ${aiFocusDraft.trim()}` : ''}...
              </Typography>
            </Box>
          )}

          {!aiSummary && !isLoadingAI && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
              Generate an AI-powered summary of the timeline activity
            </Typography>
          )}

          {aiSummary && !isLoadingAI && (
            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
                {aiDetail === 'short' ? 'Short' : 'Detailed'}
                {aiFocus ? ` · ${aiFocus}` : ' · full timeline'}
              </Typography>
              <Box
                sx={{
                  p: 2,
                  bgcolor: 'background.paper',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5,
                }}
              >
                {aiBlocks.map((block, i) => (
                  <Box key={`${block.heading ?? 'body'}-${i}`} sx={{ borderLeft: '3px solid', borderColor: 'primary.main', pl: 1.25 }}>
                    {block.heading && (
                      <Typography
                        sx={{
                          fontSize: '0.7rem',
                          fontWeight: 800,
                          letterSpacing: 0.9,
                          textTransform: 'uppercase',
                          color: 'primary.main',
                          mb: 0.5,
                        }}
                      >
                        {block.heading}
                      </Typography>
                    )}
                    {block.lines.map((line, j) =>
                      line.kind === 'li' ? (
                        <Typography
                          key={j}
                          variant="body2"
                          sx={{
                            lineHeight: 1.55,
                            pl: 1.4,
                            position: 'relative',
                            '&::before': { content: '"•"', position: 'absolute', left: 0, color: 'primary.main' },
                          }}
                        >
                          {line.text}
                        </Typography>
                      ) : (
                        <Typography key={j} variant="body2" sx={{ lineHeight: 1.65, mb: 0.75 }}>
                          {line.text}
                        </Typography>
                      ),
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      )}
      {/* Export Loading Notification - Top */}
      {isExporting && (
        <Paper
          sx={{
            position: 'absolute',
            top: 8,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9999,
            p: 2,
            px: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            bgcolor: 'rgba(30, 40, 55, 0.98)',
            border: '1px solid rgba(25, 118, 210, 0.5)',
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.5)'
          }}
        >
          <CircularProgress size={24} sx={{ color: '#1976d2' }} />
          <Box>
            <Typography variant="subtitle2" sx={{ color: '#fff', fontWeight: 'bold' }}>
              Exporting to PowerPoint
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
              {exportStatus}
            </Typography>
          </Box>
        </Paper>
      )}

      {/* Theme Selection Dialog */}
      <Dialog 
        open={themeDialogOpen} 
        onClose={() => setThemeDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'background.paper',
            backgroundImage: 'none',
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="h6" component="span">
                Select PowerPoint Theme
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Choose a visual style for your exported presentation
              </Typography>
            </Box>
            <IconButton
              edge="end"
              color="inherit"
              onClick={() => setThemeDialogOpen(false)}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            {(Object.entries(THEME_DEFINITIONS) as [PowerPointTheme, typeof THEME_DEFINITIONS[PowerPointTheme]][]).map(([themeKey, themeDef]) => (
              <Grid item xs={12} sm={6} md={4} key={themeKey}>
                <Card 
                  variant="outlined"
                  sx={{ 
                    cursor: 'pointer',
                    border: selectedTheme === themeKey ? '2px solid' : '1px solid',
                    borderColor: selectedTheme === themeKey ? 'primary.main' : 'divider',
                    bgcolor: selectedTheme === themeKey ? 'action.selected' : 'background.paper',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: 'primary.light',
                      bgcolor: 'action.hover',
                    }
                  }}
                >
                  <CardActionArea onClick={() => setSelectedTheme(themeKey)}>
                    {/* Theme Preview */}
                    <Box 
                      sx={{ 
                        height: 80, 
                        background: `linear-gradient(135deg, #${themeDef.colors.titleBg} 0%, #${themeDef.colors.primary}40 50%, #${themeDef.colors.contentBg} 100%)`,
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {/* Accent line */}
                      <Box 
                        sx={{ 
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          height: 4,
                          bgcolor: `#${themeDef.colors.accent}`
                        }}
                      />
                      {/* Selected indicator */}
                      {selectedTheme === themeKey && (
                        <Box 
                          sx={{ 
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            bgcolor: 'primary.main',
                            borderRadius: '50%',
                            width: 24,
                            height: 24,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <CheckIcon sx={{ fontSize: 16, color: 'white' }} />
                        </Box>
                      )}
                    </Box>
                    <CardContent sx={{ py: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Radio 
                          checked={selectedTheme === themeKey}
                          size="small"
                          sx={{ p: 0 }}
                        />
                        <Typography variant="subtitle2" fontWeight={600}>
                          {themeDef.name}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        {themeDef.description}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setThemeDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={() => exportToPowerPoint(selectedTheme)} 
            variant="contained" 
            color="primary"
            startIcon={<PowerPointIcon />}
          >
            Export with {THEME_DEFINITIONS[selectedTheme].name}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StoryMode;
